import type { Blueprint } from '@builder/base/blueprints/Blueprint';
import type { ValidatableBlueprint } from '@builder/base/blueprints/ValidatableBlueprint';
import type { HasWidth } from '@builder/base/traits/HasWidth';
import type { ValidationErrors } from '@builder/base/types/ValidationErrors';
import type { FormBuilderContract } from '@builder/form';
import type { BlueprintDefinition } from '@builder/form/blueprints/BlueprintDefinition';
import type { CustomErrorBlueprint } from '@builder/form/blueprints/CustomErrorBlueprint';
import type { ReadonlyBlueprint } from '@builder/form/blueprints/ReadonlyBlueprint';
import type { RequiredBlueprint } from '@builder/form/blueprints/RequiredBlueprint';
import type { VisibleBlueprint } from '@builder/form/blueprints/VisibleBlueprint';
import type { Entry } from '@builder/form/entries/Entry';
import { entry, instanceOfEntry } from '@builder/form/entries/Entry';
import { ValidEntry } from '@builder/form/entries/ValidEntry';
import { AlwaysChoice } from '@builder/form/enums/AlwaysChoice';
import type { InternallyChoice } from '@builder/form/enums/InternallyChoice';
import { NeverChoice } from '@builder/form/enums/NeverChoice';
import type { WhenChoice } from '@builder/form/enums/WhenChoice';
import type { EntryFactory } from '@builder/form/traits/EntryFactory';
import type { HasFilter } from '@builder/form/traits/HasFilter';
import type { HasHelp } from '@builder/form/traits/HasHelp';
import type { HasLabel } from '@builder/form/traits/HasLabel';
import type { HasPlaceholder } from '@builder/form/traits/HasPlaceholder';
import type { IsIndexable } from '@builder/form/traits/IsIndexable';
import type { Relational } from '@builder/form/traits/Relational';
import type { ProcessCallback } from '@builder/form/types/ProcessCallback';

export const Definition: BlueprintDefinition = {
    type: 'related-self',
    name: '[[[Hierarchia]]]',
    icon: 'fa-list-tree',
    group: 'relational',
    position: 2,
    disabled: false
};

export class RelatedSelfEntry extends ValidEntry<number>
{
    public type: string = Definition.type;
    public data: number = null;

    public constructor(data: any = null)
    {
        super();

        if (data !== null)
        {
            this.data = data;
        }
    }

    public async collect(blueprint: RelatedSelfContract, form: FormBuilderContract, preprocess: ProcessCallback): Promise<Entry>
    {
        const result = await preprocess(blueprint, this, form.blueprintId, form.entryId);

        return entry({
            type: this.type,
            data: this.data,
            ...(result ?? {})
        });
    }

    public validate(blueprint: RelatedSelfContract, form: FormBuilderContract): boolean
    {
        this.errors = {};

        if (!form.expressions.readonly(blueprint, true) && form.expressions.visible(blueprint, true))
        {
            if (form.expressions.required(blueprint) && (this.data == null || this.data == 0))
            {
                this.errors.value = [`[[[Pole "%0" jest wymagane.|||${form.localization.translate(blueprint.label)}]]]`];
            }
            else if (form.expressions.customError(blueprint))
            {
                this.errors.custom = [form.expressions.customErrorMessage(blueprint)];
            }
        }

        return this.valid();
    }
}

export const instanceOfRelatedSelfEntry = (object: any): object is RelatedSelfEntry =>
{
    return instanceOfEntry(object) && 'type' in object && object.type === Definition.type;
};

export interface RelatedSelfContract extends Blueprint, VisibleBlueprint, ReadonlyBlueprint, RequiredBlueprint, CustomErrorBlueprint, HasLabel, HasPlaceholder, HasHelp, HasWidth, HasFilter, Relational, IsIndexable
{
}

export class RelatedSelfType implements RelatedSelfContract, ValidatableBlueprint, EntryFactory<RelatedSelfEntry>
{
    public id: string;
    public type: string;
    public name: string;
    public label: Record<string, string>;
    public showLabel: boolean;
    public placeholder: Record<string, string>;
    public componentId: string;
    public dependsOn: string[];
    public help: Record<string, string>;
    public minWidth: number;
    public width: number;
    public visible: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public visibleWhen: string;
    public readonly: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public readonlyWhen: string;
    public required: AlwaysChoice | NeverChoice | WhenChoice;
    public requiredWhen: string;
    public customError: NeverChoice | WhenChoice;
    public customErrorWhen: string;
    public customErrorMessage: Record<string, string>;
    public errors: ValidationErrors;
    public showFilter: boolean;
    public indexEnabled: boolean;

    public constructor(id: string, name: string)
    {
        this.id = id;
        this.type = Definition.type;
        this.name = name;
        this.label = { 'pl-PL': 'Element nadrzędny' };
        this.showLabel = true;
        this.placeholder = {};
        this.componentId = '';
        this.dependsOn = [];
        this.help = {};
        this.minWidth = 1;
        this.width = 0;
        this.customError = NeverChoice.Never;
        this.customErrorWhen = null;
        this.customErrorMessage = {};
        this.readonly = NeverChoice.Never;
        this.readonlyWhen = null;
        this.required = NeverChoice.Never;
        this.requiredWhen = null;
        this.visible = AlwaysChoice.Always;
        this.visibleWhen = null;
        this.errors = {};
        this.showFilter = false;
        this.indexEnabled = false;
    }

    public setDefaultWidth(width: number): void
    {
        this.width = Math.min(3, Math.max(this.minWidth, width));
    }

    public createEntry(data: any): RelatedSelfEntry
    {
        return new RelatedSelfEntry(data);
    }

    public validate(): Record<string, ValidationErrors>
    {
        this.errors = {};

        if (!this.componentId)
            this.errors.componentId = ['[[[Wymagane jest wybranie komponentu]]]'];

        return {
            [this.name]: this.errors
        };
    }
}
